import React, { useEffect, useMemo, useState } from "react"
import {
    MdCancel,
    MdCheckCircleOutline,
    MdContentCopy,
    MdDeleteOutline,
    MdErrorOutline,
    MdExpandLess,
    MdExpandMore,
    MdHistory,
    MdOutlineRefresh,
    MdPersonOutline,
    MdSchedule,
    MdSync,
} from "react-icons/md"
import { toast } from "react-toastify"
import Loading from "../layouts/Loading"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import styles from "./managementLog.module.css"

const statusConfig = {
    pending: { label: "Na fila", icon: MdSchedule, tone: "pending" },
    processing: { label: "Em processamento", icon: MdSync, tone: "processing" },
    cancel_requested: { label: "Cancelamento solicitado", icon: MdSync, tone: "processing" },
    completed: { label: "Concluído", icon: MdCheckCircleOutline, tone: "completed" },
    completed_with_errors: { label: "Concluído com falhas", icon: MdErrorOutline, tone: "failed" },
    failed: { label: "Falha", icon: MdErrorOutline, tone: "failed" },
    canceled: { label: "Cancelado", icon: MdCancel, tone: "canceled" },
}

const activeStatuses = ["pending", "processing", "cancel_requested"]
const issueStatuses = ["failed", "completed_with_errors"]

const BulkActionHistory = ({
    data,
    title = "Histórico de ações em massa",
    description,
    isLoading,
    onRefresh,
    onCancel,
    cancelText,
    progressLabel,
    issueLabel = "Com ocorrências",
    getActionIcon,
    getMetadata = () => [],
    getDetails = () => [],
    getError = item => item.error,
    getActions = () => [],
    showProgress = () => true,
    headerAction,
}) => {
    const [expandedIds, setExpandedIds] = useState([])
    const [idCancel, setIdCancel] = useState("")

    const summary = useMemo(() => ({
        total: data.length,
        active: data.filter(item => activeStatuses.includes(item.status)).length,
        completed: data.filter(item => item.status === "completed").length,
        issues: data.filter(item => issueStatuses.includes(item.status)).length,
    }), [data])

    useEffect(() => {
        if (!summary.active) return undefined
        const interval = setInterval(() => onRefresh({ silent: true }), 10000)
        return () => clearInterval(interval)
    }, [summary.active, onRefresh])

    const toggleDetails = id => setExpandedIds(previous => previous.includes(id)
        ? previous.filter(itemId => itemId !== id)
        : [...previous, id]
    )

    const copyExecutionId = async id => {
        try {
            await navigator.clipboard.writeText(id)
            toast.success("Identificador copiado!")
        } catch {
            toast.error("Não foi possível copiar o identificador.")
        }
    }

    const handleCancel = async () => {
        const canceled = await onCancel(idCancel)
        if (canceled !== false) setIdCancel("")
    }

    return (
        <div className={styles.container}>
            <header className={styles.pageHeader}>
                <div>
                    <span className={styles.eyebrow}><MdHistory /> Auditoria operacional</span>
                    <h1>{title}</h1>
                    <p>{description}</p>
                </div>
                <div className={styles.headerActions}>
                    {headerAction}
                    <button type="button" className={styles.refreshButton} onClick={() => onRefresh()} disabled={isLoading}>
                        <MdOutlineRefresh />
                        Atualizar
                    </button>
                </div>
            </header>

            <section className={styles.summaryGrid} aria-label="Resumo das execuções">
                <div><span>Total</span><strong>{summary.total}</strong></div>
                <div className={styles.summaryActive}><span>Em andamento</span><strong>{summary.active}</strong></div>
                <div className={styles.summaryCompleted}><span>Concluídas</span><strong>{summary.completed}</strong></div>
                <div className={styles.summaryFailed}><span>{issueLabel}</span><strong>{summary.issues}</strong></div>
            </section>

            <section className={styles.historyList}>
                {data.map(item => {
                    const status = statusConfig[item.status] || statusConfig.pending
                    const StatusIcon = status.icon
                    const ActionIcon = getActionIcon(item) || MdHistory
                    const expanded = expandedIds.includes(item._id)
                    const progress = item.status === "completed" ? 100 : Math.min(100, Math.max(0, Number(item.progress) || 0))
                    const error = getError(item)
                    const details = getDetails(item)
                    const actions = getActions(item)

                    return (
                        <article className={styles.historyCard} key={item._id}>
                            <div className={styles.cardHeader}>
                                <div className={styles.actionIdentity}>
                                    <span className={styles.actionIcon}><ActionIcon /></span>
                                    <div>
                                        <span>{item.action_label || "Alteração"}</span>
                                        <strong>{item.action_summary || "Configuração atualizada"}</strong>
                                    </div>
                                </div>
                                <span className={`${styles.statusBadge} ${styles[status.tone]}`}>
                                    <StatusIcon />
                                    {status.label}
                                </span>
                            </div>

                            <div className={styles.metadata}>
                                <span><MdPersonOutline /> {item.user_created || "Usuário não identificado"}</span>
                                <span><MdSchedule /> {item.created_at || "Data não informada"}</span>
                                {getMetadata(item).map(({ icon: Icon, value }, index) => (
                                    <span key={`${item._id}-metadata-${index}`}><Icon /> {value}</span>
                                ))}
                            </div>

                            {showProgress(item) &&
                                <div className={styles.progressArea}>
                                    <div>
                                        <span>{progressLabel}</span>
                                        <strong>{item.count_updated || 0} de {item.count || 0} · {progress}%</strong>
                                    </div>
                                    <div className={styles.progressTrack} aria-label={`${progress}% processado`}>
                                        <span style={{ width: `${progress}%` }} />
                                    </div>
                                </div>
                            }

                            {error &&
                                <div className={styles.errorMessage}>
                                    <MdErrorOutline />
                                    <span>{error}</span>
                                </div>
                            }

                            {expanded &&
                                <div className={styles.details}>
                                    {details.map((detail, index) => (
                                        <div className={detail.items ? styles.filterDetails : undefined} key={`${item._id}-detail-${index}`}>
                                            <span>{detail.label}</span>
                                            {detail.items
                                                ? <ul>{detail.items.map((value, itemIndex) => <li key={`${value}-${itemIndex}`}>{value}</li>)}</ul>
                                                : <strong className={detail.executionId ? styles.executionId : undefined}>{detail.value}</strong>
                                            }
                                        </div>
                                    ))}
                                </div>
                            }

                            <footer className={styles.cardActions}>
                                <button type="button" className={styles.detailsButton} onClick={() => toggleDetails(item._id)}>
                                    {expanded ? <MdExpandLess /> : <MdExpandMore />}
                                    {expanded ? "Ocultar detalhes" : "Ver detalhes"}
                                </button>
                                <button type="button" className={styles.detailsButton} onClick={() => copyExecutionId(item._id)}>
                                    <MdContentCopy />
                                    Copiar ID
                                </button>
                                {actions.map((action, index) => {
                                    const ActionIcon = action.icon
                                    return (
                                        <button
                                            type="button"
                                            className={action.tone === "danger" ? styles.cancelButton : styles.detailsButton}
                                            disabled={action.disabled}
                                            onClick={() => action.onClick(item)}
                                            key={`${item._id}-action-${index}`}
                                        >
                                            {ActionIcon && <ActionIcon />}
                                            {action.label}
                                        </button>
                                    )
                                })}
                                {item.can_cancel &&
                                    <button type="button" className={styles.cancelButton} onClick={() => setIdCancel(item._id)}>
                                        <MdDeleteOutline />
                                        Cancelar execução
                                    </button>
                                }
                                {["processing", "cancel_requested"].includes(item.status) &&
                                    <button type="button" className={styles.reloadAction} onClick={() => onRefresh()}>
                                        <MdSync />
                                        Atualizar andamento
                                    </button>
                                }
                            </footer>
                        </article>
                    )
                })}

                {!data.length && !isLoading &&
                    <div className={styles.emptyState}>
                        <MdHistory />
                        <strong>Nenhuma ação registrada</strong>
                        <span>As próximas ações em massa aparecerão aqui com seus detalhes.</span>
                    </div>
                }
            </section>

            {idCancel && onCancel &&
                <ModalConfirm
                    icon={<MdDeleteOutline />}
                    title="Cancelar execução"
                    description="Deseja realmente cancelar esta execução?"
                    text={cancelText}
                    onSubmit={handleCancel}
                    closeModal={() => setIdCancel("")}
                />
            }
            {isLoading && <Loading fullPage={true} />}
        </div>
    )
}

export default BulkActionHistory
